import { useMemo, useState } from 'react';
import { Drawer } from 'antd';

import { Button } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useDanhMucQuyen } from '../hooks/useVaiTro';
import {
  khacNhau,
  loiTheoConTro,
  loiTheoQuyen,
  quyenNgoaiDanhMuc,
  sapTheoDanhMuc,
} from '../nghiep-vu/vai-tro';
import type { Role } from '../types';
import { ChonQuyen } from './ChonQuyen';

/**
 * Ngăn phân quyền cho một vai trò (`PUT /api/v1/roles/{id}/permissions`) —
 * thay **cả tập** quyền.
 *
 * Quyền vai trò đang giữ mà danh mục không còn (module đã gỡ) được nói ra
 * trước khi lưu: gửi lại chúng là `422`, nên lần lưu bỏ chúng. Lỗi `422` theo
 * `pointer` `/permissions/{i}` hiện dưới đúng ô quyền.
 *
 * Ngăn dựng lại sạch mỗi lần mở (`key` ở phía gọi).
 */
export function NganPhanQuyen({
  vaiTro,
  open,
  dangLuu,
  onClose,
  onLuu,
}: {
  vaiTro: Role;
  open: boolean;
  dangLuu: boolean;
  onClose: () => void;
  onLuu: (permissions: string[]) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const danhMuc = useDanhMucQuyen(open);

  const [daChon, setDaChon] = useState<Set<string>>(() => new Set(vaiTro.permissions));
  const daLuu = useMemo(() => new Set(vaiTro.permissions), [vaiTro.permissions]);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; quyen: Record<string, string> } | null>(null);

  const ngoaiDanhMuc = useMemo(
    () => (danhMuc.data ? quyenNgoaiDanhMuc(vaiTro.permissions, danhMuc.data) : []),
    [danhMuc.data, vaiTro.permissions],
  );
  const seGui = sapTheoDanhMuc(daChon, danhMuc.data ?? []);
  const coThayDoi = Boolean(danhMuc.data) && khacNhau(seGui, vaiTro.permissions);

  const gui = async () => {
    if (!coThayDoi || dangLuu) return;
    setLoiServer(null);
    try {
      await onLuu(seGui);
    } catch (err) {
      const e = err as ApiError;
      setLoiServer({ loi: e, quyen: loiTheoQuyen(loiTheoConTro(e.details), seGui) });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('vaiTroLop2.phanQuyenTieuDe', { ten: vaiTro.name })}
      placement="right"
      // Rộng để chia đôi (danh sách module | quyền); màn hẹp hơn thì ngăn co theo màn.
      size={960}
      styles={{ wrapper: { maxWidth: '100vw' }, body: { display: 'flex', flexDirection: 'column', minHeight: 0 } }}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiServer && <LoiTrongHop>{dichLoi(loiServer.loi)}</LoiTrongHop>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-content-muted" role="status">
              {t('vaiTroLop2.daChonSoQuyen', { so: seGui.length })}
            </span>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="md" onClick={onClose}>
                {t('chung.huy')}
              </Button>
              <Button
                variant="primary"
                size="md"
                loading={dangLuu}
                disabled={!coThayDoi}
                onClick={() => void gui()}
              >
                {dangLuu ? t('chung.dangLuu') : t('vaiTroLop2.luuPhanQuyen')}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <p className="text-sm leading-relaxed text-content-secondary">
          {t('vaiTroLop2.phanQuyenMo')}{' '}
          <span className="break-all font-mono text-xs text-content-muted">{vaiTro.code}</span>
        </p>
        {ngoaiDanhMuc.length > 0 && (
          <div
            role="status"
            className="rounded border border-warning-100 bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700"
          >
            {t('vaiTroLop2.quyenNgoaiDanhMuc', { so: ngoaiDanhMuc.length })}{' '}
            <span className="break-all font-mono">{ngoaiDanhMuc.join(', ')}</span>
          </div>
        )}
        <ChonQuyen
          danhMuc={danhMuc.data}
          dangTai={danhMuc.isLoading}
          loi={danhMuc.error}
          onThuLai={() => void danhMuc.refetch()}
          daChon={daChon}
          onDoi={(moi) => {
            setDaChon(moi);
            if (loiServer && Object.keys(loiServer.quyen).length > 0) setLoiServer(null);
          }}
          loiTheoQuyen={loiServer?.quyen}
          voHieu={dangLuu}
          chiaDoi={{ daLuu }}
        />
      </div>
    </Drawer>
  );
}
